import type React from "react"
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Menu, LayoutGrid, User, Settings, Building2 } from "lucide-react"
import DropdownChevron from "./dropdown-chevron"
import type { RootState } from "../app/store/store"
import { setActiveMenuId } from "../features/sidebar/sidebarSlice"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { ensureCurrentUser } from "../features/auth/authSlice"
import { ProfileImage } from "./ProfileImage"

interface MenuItem {
  id: string
  label: string
  icon: React.ReactNode
  href?: string
  submenu?: MenuItem[]
}

// Menu items for users with group (regular users)
const menuItemsWithGroup: MenuItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: <LayoutGrid size={20} />,
    href: "/dashboard",
  },
  {
    id: "jobs",
    label: "Jobs",
    icon: <Menu size={20} />,
    submenu: [
      { id: "all-jobs", label: "All jobs", icon: null, href: "/all-jobs" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: <Settings size={20} />,
    href: "/profile-setting",
  },
]

// Menu items for hiring managers (Dashboard, Jobs with only "All jobs", and Settings)
const menuItemsForHiringManager: MenuItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: <LayoutGrid size={20} />,
    href: "/dashboard",
  },
  {
    id: "jobs",
    label: "Jobs",
    icon: <Menu size={20} />,
    submenu: [
      { id: "all-jobs", label: "All jobs", icon: null, href: "/all-jobs" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: <Settings size={20} />,
    href: "/profile-setting",
  },
]

// Menu items for company admins
const menuItemsWithoutGroup: MenuItem[] = [
  {
    id: "company-profile",
    label: "Company profile",
    icon: <User size={20} />,
    submenu: [
      { id: "assign-roles", label: "User overview", icon: null, href: "/all-members" },

      { id: "create-user", label: "Create a new user", icon: null, href: "/create-member" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: <Settings size={20} />,
    href: "/profile-setting",
  },
]

// Menu items for super admin (empty groups)
/** Submenus that stay open by default; parent row does not toggle them closed. */
const ALWAYS_OPEN_SUBMENU_IDS = new Set<string>(["company-profile"])

const menuItemsForSuperAdmin: MenuItem[] = [
  {
    id: "super-admin-dashboard",
    label: "Dashboard",
    icon: <LayoutGrid size={20} />,
    href: "/super-admin-dashboard",
  },
  {
    id: "company",
    label: "Company",
    icon: <Building2 size={20} />,
    submenu: [
      { id: "company-list", label: "Company list", icon: null, href: "/company-list" },
      { id: "create-company", label: "Create company", icon: null, href: "/company-profile" },
    ],
  },
]

export default function Sidebar() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const activeItem = useSelector((state: RootState) => state.sidebar.activeMenuId)
  /** Full sidebar width (w-64) vs icon rail (w-20) */
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["company-profile"])
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  const [userGroups, setUserGroups] = useState<string[]>([])
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const companyProfilePictureId = useAppSelector((state) => state.auth.companyProfilePictureId)
  const companyProfilePictureUrl = useAppSelector((state) => state.auth.companyProfilePictureUrl)

  useEffect(() => {
    const handleResize = () => {
      const isSmall = window.innerWidth < 768
      setIsSmallScreen(isSmall)
      if (isSmall) {
        setIsSidebarOpen(false)
      } else {
        setIsSidebarOpen(true)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const loadUserData = async () => {
      if (!accessToken) return

      try {
        const ensuredUser = await dispatch(ensureCurrentUser()).unwrap()
        const groups = ensuredUser.groups ?? []
        setUserGroups(groups)
      } catch (error) {
        console.error('Failed to fetch current user:', error)
      }
    }

    loadUserData()
  }, [accessToken, dispatch])

  const toggleMenu = (menuId: string) => {
    if (ALWAYS_OPEN_SUBMENU_IDS.has(menuId)) {
      return
    }
    setExpandedMenus((prev) => (prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]))
  }

  const getMenuItemsForUser = (): MenuItem[] => {
    // If userGroups is empty, show super admin menu
    if (userGroups.length === 0) {
      return menuItemsForSuperAdmin
    }

    const isHiringManager = userGroups.includes("company_hiring_manager")
    const isAdmin = userGroups.includes("company_admin")
    const isRecruiter = userGroups.includes("company_recruiter")

    // company_admin: Company profile, Settings
    if (isAdmin) {
      return menuItemsWithoutGroup
    }

    // If user has recruiter role (with or without hiring manager): Dashboard, Jobs, Settings
    if (isRecruiter) {
      return menuItemsWithGroup
    }

    // company_hiring_manager (without recruiter): Jobs (All jobs only), Settings
    if (isHiringManager) {
      return menuItemsForHiringManager
    }

    // Default: return empty array or fallback
    return []
  }

  const renderMenuItems = (items: MenuItem[], isSubmenu = false) => {
    return items.map((item) => {
      const isSubmenuOpen =
        ALWAYS_OPEN_SUBMENU_IDS.has(item.id) || expandedMenus.includes(item.id)
      const hasSubmenu = item.submenu && item.submenu.length > 0
      const isActive = activeItem === item.id

      return (
        <div key={item.id}>
          <button
            onClick={() => {
              if (hasSubmenu) {
                toggleMenu(item.id)
              } else {
                dispatch(setActiveMenuId(item.id))
                if (item.href) {
                  navigate(item.href)
                }
              }
            }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all ${isSubmenu ? "pl-8 text-sm" : ""
              } ${isActive
                ? "bg-gray-100 text-gray-900 font-medium border-l-4 border-[#B8E211]"
                : "text-gray-700 hover:bg-gray-50"
              }`}
          >
            {item.icon && !isSubmenu && <span className="flex-shrink-0">{item.icon}</span>}
            {!isSidebarOpen && !isSubmenu ? (
              <span className="sr-only">{item.label}</span>
            ) : (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {hasSubmenu && !isSubmenu && !isSidebarOpen && (
                  <DropdownChevron isOpen={isSubmenuOpen} />
                )}
              </>
            )}
          </button>

          {/* Submenu items: visible when the row is open and the sidebar is in full (label) mode */}
          {hasSubmenu && isSubmenuOpen && isSidebarOpen && (
            <div className="space-y-1">{renderMenuItems(item.submenu ?? [], true)}</div>
          )}
        </div>
      )
    })
  }

  return (
    <aside
      className={`min-h-screen min-w-12 bg-white border-r border-gray-200 transition-all duration-300 overflow-y-auto flex flex-col ${isSidebarOpen ? "w-64" : "w-20"
        }`}
    >
      <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-md shadow-sm border border-black hover:bg-gray-50 transition-colors overflow-hidden">
          <ProfileImage
            profilePicture={companyProfilePictureId}
            profilePictureUrl={companyProfilePictureUrl}
            accessToken={accessToken || undefined}
            alt="Company"
            className="w-10 h-10 object-cover grayscale-[0.2]"
            fallbackPath="/assets/images/Fallback-Company-No-Image.jpg"
          />
        </div>
        {!isSmallScreen && (
          <button
            type="button"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="h-8 w-8 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
            aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {isSidebarOpen ? <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.66667 15C1.20833 15 0.815972 14.8368 0.489583 14.5104C0.163194 14.184 0 13.7917 0 13.3333V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H13.3333C13.7917 0 14.184 0.163194 14.5104 0.489583C14.8368 0.815972 15 1.20833 15 1.66667V13.3333C15 13.7917 14.8368 14.184 14.5104 14.5104C14.184 14.8368 13.7917 15 13.3333 15H1.66667ZM10.8333 13.3333H13.3333V1.66667H10.8333V13.3333ZM9.16667 13.3333V1.66667H1.66667V13.3333H9.16667Z" fill="#1C1B1F" />
            </svg>
              : <Menu size={18} />}
          </button>
        )}
      </div>

      <nav className="flex-1 px-2 py-4 space-y-1">
        {renderMenuItems(getMenuItemsForUser())}
      </nav>

      <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-4 space-y-2">
        <div className="w-10 h-10 flex items-center justify-center bg-white rounded-md shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors overflow-hidden">
          <img src="/assets/images/sideBar.png" alt="Logo" className="w-10 h-10 rounded-md object-cover grayscale-[0.2]" />
        </div>
        <div className={`text-xs text-gray-400 ${isSidebarOpen ? "" : "text-center"}`}>{isSidebarOpen ? "© 2026" : "©"}</div>
      </div>
    </aside>
  )
}
